import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const comparisons = [
  { slug: 'zendesk-vs-zurihub', title: 'Zendesk vs ZuriHub', summary: 'See how ZuriHub beats Zendesk on AI, WhatsApp, voice, and transparent pricing.' },
  { slug: 'intercom-vs-zurihub', title: 'Intercom vs ZuriHub', summary: 'Full support stack vs chat-first marketing. Compare tickets, knowledge base, and AI.' },
  { slug: 'freshdesk-vs-zurihub', title: 'Freshdesk vs ZuriHub', summary: 'Modern channels, AI, and website analytics — all in one workspace.' },
  { slug: 'zendesk-vs-freshdesk', title: 'Zendesk vs Freshdesk', summary: 'Head-to-head of the two legacy help desks and where ZuriHub fits.' },
  { slug: 'tidio-vs-zurihub', title: 'Tidio vs ZuriHub', summary: 'Chat and chatbots vs full tickets, SLAs, knowledge base, and modern channels.' },
  { slug: 'kustomer-vs-zurihub', title: 'Kustomer vs ZuriHub', summary: 'Avoid the eight-seat minimum and AI add-ons with ZuriHub at $25 per agent.' },
  { slug: 'gorgias-vs-zurihub', title: 'Gorgias vs ZuriHub', summary: 'Ecommerce helpdesk vs a complete CX workspace without ticket-based billing.' },
];

const faqs = [
  { q: 'How is ZuriHub different from Zendesk, Intercom, or Freshdesk?', a: 'ZuriHub combines messaging, tickets, AI, knowledge base, voice, and website analytics in one platform with transparent per-agent pricing and fewer add-ons.' },
  { q: 'What is the Pro price compared to competitors?', a: 'ZuriHub Pro is $25 per agent per month and includes all channels and AI. Heatmaps is an additional $15 per workspace.' },
  { q: 'Can ZuriHub replace my entire support stack?', a: 'Yes. Teams use ZuriHub for live chat, tickets, knowledge base, WhatsApp, Instagram, voice, and analytics.' },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Compare ZuriHub',
  description: 'Compare ZuriHub to Zendesk, Intercom, Freshdesk, and more.',
  url: `${site.baseUrl}/compare`,
  hasPart: comparisons.map((c) => ({
    '@type': 'WebPage',
    name: c.title,
    url: `${site.baseUrl}/compare/${c.slug}`,
  })),
};

export default function Compare() {
  return (
    <>
      <SEO
        title="Compare"
        description="Compare ZuriHub to Zendesk, Intercom, Freshdesk, and more. Side-by-side on pricing, AI, channels, and support depth."
        path="/compare"
        jsonLd={[jsonLd]}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Compare <span className="gradient-text">ZuriHub</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Honest side-by-side comparisons with the tools you are already using — or considering.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Start a free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="comparisons-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="comparisons-heading" className="section-title text-center">Pick a comparison</h2>
          <p className="section-subtitle mx-auto mt-4 text-center max-w-2xl">
            Each page breaks down features, pricing, and why teams are switching to ZuriHub.
          </p>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {comparisons.map((c) => (
              <Link
                key={c.slug}
                to={`/compare/${c.slug}`}
                className="group rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <h3 className="text-xl font-bold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{c.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition group-hover:gap-2">
                  Read comparison <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">See the difference for yourself</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start a free 14-day Pro trial and compare ZuriHub with your current tool firsthand.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
